import React, { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getAuth,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword,
  signOut,
  setPersistence,
  inMemoryPersistence,
} from 'firebase/auth';
import { app } from '../../firebase.config';

// The invitation flow runs on its OWN Firebase app instance, isolated from
// the portal's default app. Whatever session the portal itself holds (a
// signed-in super-admin who opens an invitation link in their own browser,
// for example) must never become the account whose password gets set below:
// with a shared auth instance, `auth.currentUser` would already be that
// admin, the email-link sign-in would be skipped, and updatePassword would
// overwrite the admin's password (review finding B1, 2026-09-06). The
// callable is bound to the same isolated app so it carries the invitee's
// ID token, not the admin's.
const INVITE_APP_NAME = 'invite';
const inviteApp = getApps().some((a) => a.name === INVITE_APP_NAME)
  ? getApp(INVITE_APP_NAME)
  : initializeApp(app.options, INVITE_APP_NAME);
const inviteAuth = getAuth(inviteApp);
const inviteFunctions = getFunctions(inviteApp, 'us-central1');

// Landing-page identity (CompanyFrontPage): Deep Harbor + steel blue.
const C = {
  bg: '#0f1620',
  card: '#16202e',
  border: '#2a3a4f',
  text: '#e8eef5',
  dim: '#96a5b4',
  accent: '#9ecbf7',
  button: '#2f66b8',
  buttonHover: '#3a77d1',
  error: '#e08585',
  ok: '#2ea183',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 6,
  border: `1px solid ${C.border}`,
  background: '#0c141d',
  color: C.text,
  fontSize: 15,
  outline: 'none',
};

const OFFICE_APP_URL = 'https://simplicity-office.web.app';

// Completes account setup for the two invitation flows that email links
// point at this domain:
//   /finishSignUp?customerId=...      -> adminDashboardF-finishCustomerSignUp
//   /finishUserSignUp?userId=...      -> adminDashboardF-finishUserSignUp
// SECURITY (fixed 2026-09-06, finding A1): the credential IS the one-time
// email link, not the accountId in the query string. The old design here
// deliberately had no dependency on the oobCode the Firebase action handler
// appends (mode=signIn&oobCode=...&apiKey=...) and instead sent
// {email, password, id} straight to the callable, which trusted them
// outright — any caller (or no caller at all) could set any account's
// password. That was an account-takeover hole.
// Now: this page signs the browser in via signInWithEmailLink (consuming
// the one-time code), sets the password itself with updatePassword, then
// calls the callable with an empty body — the callable trusts only
// request.auth.uid and flips emailVerified on the Firestore doc; it never
// touches Auth. accountId parsed from the query string below is kept ONLY
// as a client-side sanity check (catches "opened a different invitation's
// link while already signed in") — it is never sent to the server.
// The callable only flips the emailVerified mirror on the customer doc. Since
// portal round 4 that mirror drives the Active/Invited status pill, so a
// swallowed failure leaves the row showing "Invited" until the next
// successful write - but it never hides a successful password save.
const FinishSignUp = ({ flow }) => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isCustomer = flow === 'customer';
  // Sanity check ONLY - never sent to the server. The real identity is
  // whichever Firebase user signInWithEmailLink signs into below
  // (request.auth.uid on the callable side).
  const accountId = params.get(isCustomer ? 'customerId' : 'userId');
  // window.location.href doesn't change while this page stays mounted, so
  // this only needs to compute once at mount - the dep array is
  // intentionally empty (the check reads window.location directly, which
  // exhaustive-deps cannot see as a reactive value).
  const linkIsValid = useMemo(
    () => isSignInWithEmailLink(inviteAuth, window.location.href),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false);
  // uid that THIS page signed in through the email link. Any other session
  // on the isolated auth instance is dropped before the password is set.
  const linkUidRef = useRef(null);

  // Recovery guidance for a dead link: shown on the full invalid-link screen
  // below AND inline when the one-time code turns out to be expired or
  // already-used at submit time (same advice, this surface is plain text).
  const recoveryHintText = isCustomer
    ? ' Open https://simplicity-office.web.app and use "Forgot password?" on the sign-in page to set a new one.'
    : ' Ask your administrator for a new invitation, or contact info@simplicitydynamics.ca.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError(null);
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // Never persisted: a half-finished sign-up (link consumed, password
      // not yet saved) must not linger signed in if the tab is closed.
      await setPersistence(inviteAuth, inMemoryPersistence);
      // Only the account this page signed in through the link may proceed;
      // anything else on the isolated instance is dropped first.
      if (inviteAuth.currentUser && inviteAuth.currentUser.uid !== linkUidRef.current) {
        await signOut(inviteAuth);
      }
      if (!inviteAuth.currentUser) {
        // Consumes the one-time code. A retry after a failure further down
        // skips this branch (the link-signed-in user is still current) -
        // the code cannot be redeemed a second time.
        const cred = await signInWithEmailLink(inviteAuth, email.trim(), window.location.href);
        if (accountId && cred.user.uid !== accountId) {
          await signOut(inviteAuth);
          setError('This link was sent for a different account. Open the link from your own invitation email.');
          return;
        }
        linkUidRef.current = cred.user.uid;
      }
      await updatePassword(inviteAuth.currentUser, password);
      // The password is saved - that is the outcome the user needs. The
      // callable only flips the emailVerified mirror on the Firestore doc
      // (it drives the portal's Active/Invited pill, nothing gates on it), so
      // it must never turn a completed sign-up into an error screen the user
      // cannot retry - at worst the portal keeps showing "Invited".
      const fnName = isCustomer
        ? 'adminDashboardF-finishCustomerSignUp'
        : 'adminDashboardF-finishUserSignUp';
      try {
        await httpsCallable(inviteFunctions, fnName)({});
      } catch (mirrorErr) {
        console.warn('emailVerified mirror not updated:', mirrorErr.code || mirrorErr.message);
      }
      await signOut(inviteAuth);
      setDone(true);
    } catch (err) {
      // A failure here (link sign-in or updatePassword) must NOT sign the
      // user out - the one-time code is already spent, so a retry needs to
      // reuse this same signed-in session (the `if (!inviteAuth.currentUser)`
      // branch above then skips signInWithEmailLink on the retry).
      switch (err.code) {
        case 'auth/invalid-action-code':
        case 'auth/expired-action-code':
          setError(`This link has expired or was already used.${recoveryHintText}`);
          break;
        case 'auth/requires-recent-login':
          setError(`This page timed out before the password was saved.${recoveryHintText}`);
          break;
        case 'auth/invalid-email':
          setError("That email address doesn't match this invitation.");
          break;
        case 'auth/password-does-not-meet-requirements':
        case 'auth/weak-password':
          setError('Please choose a stronger password (at least 12 characters).');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please wait a few minutes and try again.');
          break;
        default:
          setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <div
      style={{
        minHeight: '100vh',
        background: C.bg,
        backgroundImage:
          'linear-gradient(rgba(158,203,247,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(158,203,247,0.05) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
        }}
      >
        <div style={{ color: C.accent, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
          Simplicity Dynamics
        </div>
        {children}
      </div>
    </div>
  );

  if (!linkIsValid) {
    return shell(
      <>
        <h1 style={{ color: C.text, fontSize: 22, fontWeight: 700, margin: '0 0 10px' }}>
          This link is not valid or was already used
        </h1>
        <p style={{ color: C.dim, fontSize: 15, lineHeight: 1.5, margin: 0 }}>
          {isCustomer ? (
            <>
              Invitation links can only be used once. Open{' '}
              <a href={OFFICE_APP_URL} style={{ color: C.accent }}>
                SimpleOffice
              </a>{' '}
              and use &quot;Forgot password?&quot; on the sign-in page to set a new one.
            </>
          ) : (
            <>
              Invitation links can only be used once. Ask your administrator for a new
              invitation, or contact{' '}
              <a href="mailto:info@simplicitydynamics.ca" style={{ color: C.accent }}>
                info@simplicitydynamics.ca
              </a>
              .
            </>
          )}
        </p>
      </>
    );
  }

  if (!accountId) {
    return shell(
      <>
        <h1 style={{ color: C.text, fontSize: 22, fontWeight: 700, margin: '0 0 10px' }}>
          This link is incomplete
        </h1>
        <p style={{ color: C.dim, fontSize: 15, lineHeight: 1.5, margin: 0 }}>
          Please open the registration link from your email again. If it keeps failing, contact{' '}
          <a href="mailto:info@simplicitydynamics.ca" style={{ color: C.accent }}>
            info@simplicitydynamics.ca
          </a>{' '}
          and we will send you a new one.
        </p>
      </>
    );
  }

  if (done) {
    return shell(
      <>
        <h1 style={{ color: C.text, fontSize: 22, fontWeight: 700, margin: '0 0 10px' }}>
          You're all set <span style={{ color: C.ok }}>✓</span>
        </h1>
        <p style={{ color: C.dim, fontSize: 15, lineHeight: 1.5, margin: '0 0 18px' }}>
          Your password has been saved. Sign in with your email and new password.
        </p>
        <a
          href={isCustomer ? OFFICE_APP_URL : '/login'}
          style={{
            display: 'block',
            textAlign: 'center',
            background: C.button,
            color: '#fff',
            padding: '11px 12px',
            borderRadius: 6,
            fontSize: 15,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {isCustomer ? 'Sign in to SimpleOffice' : 'Go to sign in'}
        </a>
      </>
    );
  }

  return shell(
    <>
      <h1 style={{ color: C.text, fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>
        Finish setting up your account
      </h1>
      <p style={{ color: C.dim, fontSize: 14.5, lineHeight: 1.5, margin: '0 0 18px' }}>
        {isCustomer
          ? 'Confirm the email this invitation was sent to and choose a password for your SimpleOffice account.'
          : 'Confirm the email this invitation was sent to and choose a password.'}
      </p>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label htmlFor="fs-email" style={{ display: 'block', color: C.dim, fontSize: 13, marginBottom: 5 }}>
              Email address
            </label>
            <input
              id="fs-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label htmlFor="fs-password" style={{ display: 'block', color: C.dim, fontSize: 13, marginBottom: 5 }}>
              New password (at least 12 characters)
            </label>
            <input
              id="fs-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label htmlFor="fs-confirm" style={{ display: 'block', color: C.dim, fontSize: 13, marginBottom: 5 }}>
              Confirm password
            </label>
            <input
              id="fs-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={inputStyle}
              autoComplete="new-password"
              required
            />
          </div>
          {error && (
            <p style={{ color: C.error, fontSize: 14, margin: 0 }}>{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting}
            style={{
              background: submitting ? '#274f8c' : C.button,
              color: '#fff',
              padding: '11px 12px',
              borderRadius: 6,
              fontSize: 15,
              fontWeight: 600,
              border: 'none',
              cursor: submitting ? 'default' : 'pointer',
            }}
          >
            {submitting ? 'Saving…' : 'Save password'}
          </button>
        </div>
      </form>
    </>
  );
};

export default FinishSignUp;
